import Main from "pages/Main"
import Product from "pages/Product"
import Pricing from "pages/Pricing"
import About from "pages/About"
import Leadership from "pages/Leadership"
import Profile from "pages/Profile"
import ContactUs from "pages/ContactUs"
import PartnerProgram from "pages/PartnerProgram"
import FAQPage from "pages/FAQPage"
import Integrations from "pages/Integrations"
import PrivacyPolicy from "pages/PrivacyPolicy"
import Security from "pages/Security"

import { products } from "./product"
import { team } from "./index"
import TermsAndConditions from "pages/TermsAndConditions"
import PageNotFound from "pages/PageNotFound"

/**
 * Every page of the site. This list drives the router, the prerendered HTML
 * files, sitemap.xml and the <head> tags (src/seo/head.js).
 *
 * seo:
 *   title        page name; the document title becomes "<title> | Maplexxon"
 *   fullTitle    exact document title (overrides the format above)
 *   description  meta / Open Graph description
 *   noindex      true -> <meta name="robots" content="noindex, follow">, left out of sitemap.xml
 *   faq          true -> FAQPage JSON-LD from the FAQ entries
 *   pricing      true -> SoftwareApplication JSON-LD with offers for every listed product
 *   product      product object (product pages)
 */
let routes = [
  {
    "path": "/",
    "seo": {
      "fullTitle": "Maplexxon | Ready-made software solutions to support your business",
      "description": "Maplexxon builds ready-made ecommerce websites, appointment booking sites and company profile websites for businesses, with a CMS, payment integration and IT support included.",
    },
    "element": <Main />
  },
  {
    "path": "/pricing",
    "seo": {
      "title": "Pricing",
      "description": "Compare Maplexxon plans for our ready-made business websites: monthly and annual prices in Rupiah, core pricing, what each plan includes and the add-ons you can buy.",
      "pricing": true,
    },
    "element": <Pricing />
  },
  {
    "path": "/about",
    "seo": {
      "title": "About Us",
      "description": "Maplexxon builds ready-made software so businesses can launch faster and spend less on development. Read about our vision, our mission and the team behind the products.",
    },
    "element": <About />
  },
  {
    "path": "/leadership",
    "seo": {
      "title": "Our Leadership",
      "description": "Meet the leadership team behind Maplexxon.",
      // Placeholder content for now; keep it out of search results.
      "noindex": true,
    },
    "element": <Leadership />
  },
  {
    "path": "/contact",
    "seo": {
      "title": "Contact Us",
      "description": "Talk to the Maplexxon team about your website or software project. Call or WhatsApp +62 811 1094 8899, email sales@maplexxon.com, or visit our office in West Jakarta.",
    },
    "element": <ContactUs />
  },
  {
    "path": "/partner-program",
    "seo": {
      "title": "Partner Program",
      "description": "Join the Maplexxon referral or reseller program. Share referral links or resell our software, earn commissions, and get a sales kit, product training and technical support.",
    },
    "element": <PartnerProgram />
  },
  {
    "path": "/faq",
    "seo": {
      "title": "FAQ",
      "description": "Answers to common questions about Maplexxon: go-live timelines, domains and hosting, custom designs, billing and cancellation, support, and connecting your existing website.",
      "faq": true,
    },
    "element": <FAQPage />
  },
  {
    "path": "/integrations",
    "seo": {
      "title": "Integrations",
      "description": "Connect your Maplexxon website with Mailchimp, Google Calendar and Google Workspace, and accept payments by credit card, GoPay, virtual account or at Indomaret.",
    },
    "element": <Integrations />
  },
  {
    "path": "/privacypolicy",
    "seo": {
      "title": "Privacy Policy",
      "description": "How Maplexxon (PT Cendekia Edukasi Citra Tritunggal) collects, uses and protects your personal data, which cookies we use, and how to ask us to delete your data.",
    },
    "element": <PrivacyPolicy />
  },
  {
    "path": "/security",
    "seo": {
      "title": "Security",
      "description": "How Maplexxon keeps your data safe: role-based access control, daily encrypted backups, encryption in transit and at rest, regular vulnerability testing and PCI DSS compliant payments.",
    },
    "element": <Security />
  },
  {
    "path": "/termsandconditions",
    "seo": {
      "title": "Terms and Conditions",
      "description": "The terms and conditions for using software, services and products provided by Maplexxon, operated by PT Cendekia Edukasi Citra Tritunggal.",
    },
    "element": <TermsAndConditions />
  },
  {
    "path": "*",
    "seo": {
      "title": "Page Not Found",
      "description": "The page you are looking for does not exist or has been moved.",
      "noindex": true,
    },
    "element": <PageNotFound />
  },
]

// Search descriptions for product pages; products without an entry use their
// own short description.
const productDescriptions = {
  "ecommerce-website": "Sell online with a ready-made ecommerce website from Maplexxon: product and inventory management, Midtrans payments, shipping options, coupons, automated emails and live chat.",
  "appointment-and-scheduling-management": "Take bookings online with a branded appointment and scheduling website: upfront payments, automated email reminders, slot management in the CMS and regular booking reports.",
  "company-profile-site": "Get a professional company profile website with a premium design, a CMS to update your content, built-in blogging and a layout that works on desktop, tablet and mobile.",
}

// Append product pages to routes (products without `details` have no page yet)
products.filter((product) => product && product.details).forEach((product) => {
  routes.push({
    "path": `/product/${product.slug}`,
    "seo": {
      "title": product.name,
      "description": productDescriptions[product.slug] || product.description,
      // Unreleased products stay reachable but out of search results.
      "noindex": Boolean(product.beta || product.comingSoon),
      "product": product,
    },
    "element": <Product />
  })
})

// Append profile pages to routes (one per slug)
team.forEach((item) => {
  const path = `/profile/${item.slug}`
  if (routes.some((route) => route.path === path)) return
  routes.push({
    "path": path,
    "seo": {
      "title": item.name,
      "description": item.role ? `${item.name}, ${item.role} at Maplexxon.` : `${item.name} at Maplexxon.`,
      // Placeholder biographies for now; keep them out of search results.
      "noindex": true,
    },
    "element": <Profile />
  })
})

export default routes
